/* eslint-disable no-secrets/no-secrets */
import {
    Loader,
    useModal
} from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { PolicyService } from 'gw-capability-policy';
import { DocumentService } from 'gw-capability-policydocument';
import { useAciUtil } from 'ent-capability-hooks';
import {
    PaymentPlans,
    TurnOnAutopay,
    PaymentMethod,
    MakeAPayment,
} from 'ent-capability-billing-react';
import { OfferOfInsurance } from 'ent-capability-offers-react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {
    useCallback,
    useEffect,
    useState
} from 'react';
import { withRouter } from 'react-router-dom';
import { CustomQuoteService } from 'twia-capability-quoteandbind-residential';
import PolicyDetailsSummary from '../../components/PolicyDetailsSummary/PolicyDetailsSummary'
import messages from './OfferDetails.messages.js';
import metadata from './OfferDetails.metadata.json5';
import styles from './OfferDetails.module.scss';
import debounce from 'debounce';


// 2025-01-08 - Debra Frum - US25427 - CE - Make a Payment on Offer Details
//   Modified interface with PaymentMethod component.
// 2025-01-02 - Debra Frum - US25427 - CE - Make a Payment on Offer Details
//   Replaced PaymentContext with page managing state between Payment components
//   Modified interface with PaymentPlans component
// 2024-12-02 - Debra Frum - US25427 - CE - Make a Payment on Offer Details
// 2024-11-04 - Debra Frum - US25431 - CE - Review and Pay Available Offers
//
const handlePrint = () => {
    window.print();
};

const goToDocumentView = () => {
    let data = document.getElementById('offerPolicyDocumentsContainer');

    if (_.isEmpty(data)) {
        data = document.getElementById('DocumentsAccordion');
    }

    data.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
    })
}


function OfferDetails(props) {
    const {
        history,
        match,
        onResolve,
        onReject,
        onAfterClose
    } = props;
    const authHeader = _.get(useAuthentication(), 'authHeader');
    const [offerCommonDetails, updateOfferCommonDetails] = useState({
        currentPeriodDetails: {}
    });
    const [enableEdit, /* setEnableEdit */] = useState(false);  // false = enabled
    const [/* calcFees */, setCalcFees] = useState(true);
    const [paymentInstruments, setPaymentInstruments] = useState([]);
    const [filteredPaymentInstruments, setFilteredPaymentInstruments] = useState([]); // single-use payment instruments filtered out when auto pay on
    const [selectedPaymentInstrumentId, setSelectedPaymentInstrumentId] = useState('');
    const [selectedPaymentInstrument, setSelectedPaymentInstrument] = useState({});
    const [paymentPlan, setPaymentPlan] = useState();
    const [baseAmount, setBaseAmount] = useState();
    const { 
        filterPaymentInstruments, 
        getSyncedPaymentInstrumentsForAccount, 
        makeOfferPayment, 
        handleAddPaymentInstrumentError
    } = useAciUtil();
    const [writableDocuments, updateWritableDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const translator = useTranslator();
    const offerNumber = _.get(match, 'params.offerNumber');
    const [autoPay, updateAutoPay] = useState(false);
    const { showAlert } = useModal();
    const [DBOP, setDBOP] = useState(() => {});

    const getOfferDetails = useCallback(async () => {
        if (authHeader) {
            const offerDetails = await PolicyService.getAccountOfferDetails(
                offerNumber,
                authHeader
            );

            // 2025-01-21 - Debra Frum - DE18028 - Amount to Pay doesn't include WPI-8 waiver surcharge when
            //   first navigating to Offer Details page
            // Initialize customer payment amount to the selected payment plan downpayment amount which includes
            // credits and surcharges
            if (offerDetails.bindData_TWIA && offerDetails.bindData_TWIA.paymentPlans) {
                const plans = offerDetails.bindData_TWIA.paymentPlans;
                const currentplan = plans.find(plan => plan.billingId === offerDetails.bindData_TWIA.selectedPaymentPlan);

                offerDetails.bindData_TWIA.customerPaymentDetails_TWIA.amount = currentplan.downPayment.amount;
            }

            // Restore state if returning from the ACI Payment Instrument page
            const url = new URL(window.location.href);
            const searchParams = new URLSearchParams(url.search);
            
            let aState = searchParams?.get('state');
            const redirectBindData = offerDetails.bindData_TWIA ? offerDetails.bindData_TWIA : {};

            if (aState) {
                aState = JSON.parse(window.atob(window.decodeURIComponent(aState)));
                
                redirectBindData.selectedPaymentPlan = _.get(aState, 'paymentPlan');
                redirectBindData.customerPaymentDetails_TWIA.amount = _.get(aState, 'baseAmount');
                redirectBindData.customerPaymentDetails_TWIA.isAutoPayEnabled =  _.get(aState, 'autoPay');
                redirectBindData.selectedPaymentInstrument = _.get(aState, 'paymentInstrument');
            }

            updateOfferCommonDetails({
                currentPeriodDetails: offerDetails.currentPeriod,
                bindData: redirectBindData,
           });
            setBaseAmount(offerDetails.bindData_TWIA?.customerPaymentDetails_TWIA?.amount);
            setPaymentPlan(offerDetails.bindData_TWIA?.selectedPaymentPlan);

            // 2025-01-23 - Debra Frum - DE18045 - Getting error popup after adding new payment method on Offer Details screen
            // To resolve ACI error resulting from multiple unnecessary retrievals of synced payment instruments,
            // lifted state of payment instruments from child to parent to be shared with all children
            const accountNumber = _.get(offerDetails, 'bindData_TWIA.accountNumber');

            await getSyncedPaymentInstrumentsForAccount(accountNumber, authHeader).then(setPaymentInstruments);

            // 2025-03-03 - Debra Frum - DE18428 - DR01- CE - Payment method is not saved when auto-pay is selected 
            //                                     for payment plans other than 10 pay
            handleAddPaymentInstrumentError(accountNumber, offerNumber, searchParams, authHeader);
            
            setLoading(false);
        }
    }, [authHeader, offerNumber, getSyncedPaymentInstrumentsForAccount, handleAddPaymentInstrumentError]);

    const setPaymentInstrument = useCallback((instrument) => {
        setSelectedPaymentInstrumentId(instrument?.publicID);
        setSelectedPaymentInstrument(instrument);
        _.set(offerCommonDetails, 'bindData.customerPaymentDetails_TWIA.token', _.get(instrument, 'token'));
    },  [offerCommonDetails, setSelectedPaymentInstrument]);

    // 2025-01-23 - Debra Frum - DE18045 - Getting error popup after adding new payment method on Offer Details screen
    // To resolve ACI error resulting from multiple unnecessary retrievals of synced payment instruments,
    // lifted state of payment instruments from child to parent to be shared with all children
    const onSelectedInstrumentChange = useCallback((value) => {
        setSelectedPaymentInstrumentId(value);

        const instrument = paymentInstruments.find(pi => pi.publicID === value);

        setSelectedPaymentInstrument(instrument);
        setPaymentInstrument(instrument);
    },  [paymentInstruments, setPaymentInstrument]);

    const setAutoPay = useCallback((value) => {
        updateAutoPay(value);
        _.set(offerCommonDetails, 'bindData.customerPaymentDetails_TWIA.isAutoPayEnabled', value);
        filterPaymentInstruments(value, paymentInstruments, setFilteredPaymentInstruments);
    },  [offerCommonDetails, paymentInstruments, filterPaymentInstruments, setFilteredPaymentInstruments]);

    const getPolicyDownloadLink = useCallback(() => {
         DocumentService.downloadPolicyDocument(
            _.get(offerCommonDetails, 'currentPeriodDetails.idCardPublicID'),
            _.get(offerCommonDetails, 'currentPeriodDetails.idCardSessionID')
        );
    }, [offerCommonDetails]);

    // 2025-01-20 - Debra Frum - US25428 - CE - Offer Bind and Confirmation
    // Define the function that routes to the Submission Successful Confirmation page
    // when the OK button on the Payment Confirmation page is clicked after payment
    // has been made and Offer is issued
    const onClickOk = useCallback((policyData) => {
        history.push({
            pathname: '/submission-successful',
            state: {
                policyData,
            },
        });
    }, [history]);

    useEffect(() => {
        setDBOP(() => debounce(makeOfferPayment, 5000, true));
    }, []);

    const onMakePayment = useCallback(async (setMakingPayment) => {
        // 2025-02-02 - Debra Frum -  US25432 - CE - Policy Holds
        // Don't send payment if there is a policy hold or other blocking UW Issues
        const checkUWIssues = async () => {
            const uwIssueStatus = await CustomQuoteService.getUWIssueStatus(offerNumber, undefined, authHeader);

            if (uwIssueStatus.anyPolicyHoldUWIssues) {
                showAlert({
                    status: 'error',
                    title: messages.noTitle,
                    message: messages.errorPolicyHoldUW,
                });
                setMakingPayment(false);
            } else if (uwIssueStatus.anyOtherBlockingUWIssues) {
                showAlert({
                    status: 'error',
                    title: messages.noTitle,
                    message: messages.cantIssuePolicy,
                });
                setMakingPayment(false);
            } else {
                const config = {
                    policyCommonDetails: offerCommonDetails,
                    setLoading: setMakingPayment, // US36408 disable spinner, using inline button now
                    authHeader,
                    onClickOk,
                    onResolve,
                    onReject,
                    onAfterClose
                };

                console.debug('makeOfferPayment - start');
                DBOP(config);
            }
        };

        try {
            checkUWIssues();
        }
        catch (e) {
            _.noop()
        }

    }, [offerNumber, showAlert, offerCommonDetails, authHeader, onClickOk, onResolve, onReject, onAfterClose, DBOP]);

    useEffect(
        () => {
            try {
                if (authHeader) {
                    getOfferDetails();
                }
            } catch (e) {
                throw new Error(e);
            }
        },
        // Only need to run once when initial page render and when documents are added/removed
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [writableDocuments.length, authHeader]
    );

    const overrideProps = {
        pageTitle: {
            content: translator(messages.pageTitle, {
                offerNumber: _.get(offerCommonDetails, 'currentPeriodDetails.jobNumber_TWIA'),
            }),
        },
        currentPeriod: {
            heading: translator(messages.currentTab),
        },
        idCardContainer: {
            visible: !_.isEmpty(_.get(offerCommonDetails, 'currentPeriodDetails.idCardPublicID')),
        },
        idCard: {
            href: getPolicyDownloadLink(),
            target: '_blank',
        },
        // offerPolicyDocumentsSection: {
        //     updateWritableDocuments: updateWritableDocuments,
        //     writableDocuments: writableDocuments,
        //     onlyPNI: true,
        //     disableUpload: true,
        // },
        offerPolicyPeriodTabSet: {
            defaultActiveTab: 'currentPeriod'
        },
        offerOfferOfInsurance: {
            offer: _.get(offerCommonDetails, 'currentPeriodDetails'), 
            bindData: _.get(offerCommonDetails, 'bindData'),
        },
        policyDetailsSummary: {
            policyCommonDetails: offerCommonDetails,
            updateWritableDocuments,
            writableDocuments,
            isPolicyLevelDoc : false
        },
        // 2025-01-02 - Debra Frum - US25427 - CE - Make a Payment on Offer Details
        // Replaced PaymentContext with page managing state between Payment components.
        offerPaymentPlans: {
            enableEdit,
            payFrom: "offers",
            authHeader,
            offer: _.get(offerCommonDetails, 'currentPeriodDetails'),
            bindData: _.get(offerCommonDetails, 'bindData'),
            baseAmount,
            setBaseAmount,
            paymentPlan,
            setPaymentPlan,
            setCalcFees
        },
        offerPaymentMethod: {
            enableEdit,
            payFrom: "offers",
            authHeader,
            offer: _.get(offerCommonDetails, 'currentPeriodDetails'),
            bindData: _.get(offerCommonDetails, 'bindData'),
            accountNumberIdentifier: _.get(offerCommonDetails, 'bindData.accountNumber'),
            // lobCode,                                                     // invoices only?
            // streamPublicId, // needed for auto pay - add payment method  // invoices only?
            onSelectedInstrumentChange,
            selectedPaymentInstrument: setPaymentInstrument,
            paymentInstrument: selectedPaymentInstrumentId,
            paymentInstruments: filteredPaymentInstruments,
            identifier: _.get(offerCommonDetails, 'currentPeriodDetails.jobNumber_TWIA'),
            isAutoDraft: autoPay
        },
        offerMakeAPayment: {
            onMakePayment,
            payFrom: 'offers',
            authHeader,
            offer: _.get(offerCommonDetails, 'currentPeriodDetails'),
            bindData: _.get(offerCommonDetails, 'bindData'),
            paymentPlan,
            setPaymentPlan,
            manualAmountToPay: {amount: _.get(offerCommonDetails, 'bindData.customerPaymentDetails_TWIA.amount'), currency: 'usd'},
            autoPay,
            accountNumberIdentifier: _.get(offerCommonDetails, 'bindData.accountNumber'),
            paymentInstrument: selectedPaymentInstrument,
            currency: 'usd',
            calcFees: true,
            setCalcFees,
            history,
        },
        offerTurnOnAutopay: {
            enableEdit,
            payFrom: "offers",
            bindData: _.get(offerCommonDetails, 'bindData'),
            paymentPlan,
            setAutoPay
        },
    };

    const resolvers = {
        resolveComponentMap: {
            // policycommondetailscomponent: PolicyCommonDetails,
            // policycoveragescomponent: PolicyCoverages,
            // policycommondocumentscomponent: PolicyCommonDocuments,
            // policycommoncontactscomponent: PolicyCommonContacts,
            policyDetailsSummary: PolicyDetailsSummary,
            offerofinsurance: OfferOfInsurance,
            paymentplans: PaymentPlans,
            turnonautopay: TurnOnAutopay,
            paymentmethod: PaymentMethod,
            makeapayment: MakeAPayment,
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onPrint: handlePrint,
            onClickDocument: goToDocumentView
        },
    };

    return (
        <Loader loaded={!loading}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={offerCommonDetails}
                overrideProps={overrideProps}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </Loader>
    );
}

OfferDetails.propTypes = {
    match: PropTypes.shape({}).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func,
    }).isRequired,
    location: PropTypes.shape({}).isRequired,
};

export default withRouter(OfferDetails);
